import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toActivitiesUrl } from '@/utils/drillthrough';
import { humanizeRiskBand } from '@/lib/ui/format';
import type { MilestoneRow } from '@/utils/analyticsCompute';

export type RiskBandFilter = 'all' | 'overdue' | 'at_risk' | 'ok' | 'unknown';

export interface MilestoneTableProps {
  milestones: MilestoneRow[];
  loading?: boolean;
  defaultTopN?: number;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString(undefined, { dateStyle: 'short' });
  } catch {
    return '—';
  }
}

const riskBadgeVariant: Record<string, 'error' | 'warning' | 'ok' | 'unknown'> = {
  overdue: 'error',
  at_risk: 'warning',
  ok: 'ok',
  unknown: 'unknown',
};

const FILTER_LABELS: Record<RiskBandFilter, string> = {
  all: 'Top 50',
  overdue: 'Overdue',
  at_risk: 'At risk',
  ok: 'OK',
  unknown: 'Unknown',
};

const TOP_AT_RISK = 50;

export const MilestoneTable: React.FC<MilestoneTableProps> = ({
  milestones,
  loading = false,
  defaultTopN = TOP_AT_RISK,
}) => {
  const [riskBand, setRiskBand] = useState<RiskBandFilter>('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    let list = milestones.filter((m) => {
      if (riskBand !== 'all' && m.riskBand !== riskBand) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const name = (m.name ?? '').toLowerCase();
        const wbs = (m.wbs_name ?? m.wbs_id ?? '').toLowerCase();
        if (!name.includes(q) && !wbs.includes(q)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => (b.varianceDays ?? 0) - (a.varianceDays ?? 0));
    if (riskBand === 'all') list = list.slice(0, defaultTopN);
    return list;
  }, [milestones, riskBand, search, defaultTopN]);

  if (loading) {
    return (
      <div className="p-3">
        <p className="text-xs text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex flex-wrap gap-1.5 mb-2 text-xs items-center">
        <span className="text-muted-foreground font-medium">Risk:</span>
        {(['all', 'overdue', 'at_risk', 'ok', 'unknown'] as const).map((b) => (
          <Button
            key={b}
            variant={riskBand === b ? 'default' : 'outline'}
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={() => setRiskBand(b)}
          >
            {FILTER_LABELS[b]}
          </Button>
        ))}
        <Input
          placeholder="Search name or WBS"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-6 w-36 text-[11px]"
        />
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No milestones match the current filters.</p>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Milestone</th>
              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">WBS</th>
              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Baseline / Original</th>
              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Current Planned</th>
              <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Variance (d)</th>
              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Risk</th>
              <th className="w-16 py-1.5 px-2 text-muted-foreground font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const viewHref = toActivitiesUrl({
                wbs_id: r.wbs_id || undefined,
                filter: 'milestones',
                atRisk: r.riskBand !== 'ok',
              });
              return (
                <tr key={r.id} className="border-b border-border/40 hover:bg-muted/20">
                  <td className="py-1.5 px-2 font-medium text-foreground truncate max-w-[180px]" title={r.name}>
                    {r.name}
                  </td>
                  <td className="py-1.5 px-2 text-muted-foreground truncate max-w-[100px]">
                    {r.wbs_name ?? r.wbs_id}
                  </td>
                  <td className="py-1.5 px-2 text-muted-foreground tabular-nums">
                    {formatDate(r.baselineOrOriginalDate)}
                  </td>
                  <td className="py-1.5 px-2 text-muted-foreground tabular-nums">
                    {formatDate(r.currentPlannedDate)}
                  </td>
                  <td
                    className={`py-1.5 px-2 tabular-nums text-right ${
                      (r.varianceDays ?? 0) > 0 ? 'text-destructive' : 'text-foreground'
                    }`}
                  >
                    {r.varianceDays != null ? r.varianceDays : '—'}
                  </td>
                  <td className="py-1.5 px-2">
                    <Badge variant={riskBadgeVariant[r.riskBand] ?? 'unknown'} size="sm">{humanizeRiskBand(r.riskBand)}</Badge>
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <Link
                      to={viewHref}
                      className="inline-flex items-center justify-end gap-0.5 text-primary text-[11px] font-medium hover:underline"
                      title="View activities"
                    >
                      View
                      <ChevronRight className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};
